/**
 * FocusModeWalkthrough — first-visit explainer modal.
 *
 * Fires once per device when a user lands on /focus and hasn't seen the
 * walkthrough yet (gated by localStorage `istudypro_focus_walkthrough_v1`).
 * 4 steps × ~30s each = a quick 2-minute mental model. Skippable any time.
 *
 * Important: we DELIBERATELY don't auto-start the timer from this modal.
 * The whole point of Focus Mode is "the user chose to focus, no surprises."
 * The modal just teaches; the user still hits Start themselves.
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Music, Flame, X, ArrowRight, ArrowLeft, Play, Coffee } from 'lucide-react';

const FLAG_KEY = 'istudypro_focus_walkthrough_v1';

const STEPS = [
  {
    icon: Play,
    accent: '#5C8AFF',
    title: "It's a Pomodoro timer with teeth.",
    body: (
      <>
        <p className="mb-3">Pick a task type. Hit start. Work for <strong>25 minutes</strong>. Take a <strong>5-min break</strong>. Repeat. Every 4th break is a longer <strong>15-min reset</strong>.</p>
        <p className="text-white/60 text-xs">That's it. No AI does the work for you. Focus Mode is the <em>scaffold</em>; you still have to put the reps in. The thing that makes it different is <strong>everything we remove</strong> — see the next step.</p>
      </>
    ),
  },
  {
    icon: X,
    accent: '#E85C5C',
    title: 'Nav. Notifications. Other tabs. Gone.',
    body: (
      <>
        <p className="mb-3">The moment you start a session, the rest of the app <strong>disappears</strong>. No sidebar. No notifications. No social-feed dopamine. Just a giant timer.</p>
        <p className="text-white/60 text-xs">Your tab title updates with the time so you can see it from another app. If you switch tabs, the timer keeps running — and your future self will know you bailed.</p>
      </>
    ),
  },
  {
    icon: Music,
    accent: '#34C759',
    title: 'Pick a sound. Or pick silence.',
    body: (
      <>
        <p className="mb-3">Top-right menu has 5 ambient presets: <strong>Silent · Lo-fi · Rain · White noise · Campfire</strong>. Your pick is remembered next time.</p>
        <p className="text-white/60 text-xs">A soft 3-note chime plays at each phase boundary so you don't have to watch the clock. The chime works offline.</p>
      </>
    ),
  },
  {
    icon: Flame,
    accent: '#FF8A3C',
    title: 'Every completed cycle becomes a streak.',
    body: (
      <>
        <p className="mb-3">Finish one 25-minute cycle today → 🔥 streak alive. Skip a whole day → streak resets. Your weekly stats card on the dashboard shows the 7-day picture.</p>
        <p className="text-white/60 text-xs">If you're in a Study Pact, your partner sees whether you showed up. That's not surveillance — it's gentle peer pressure. Both of you get stronger for it.</p>
      </>
    ),
  },
];

export default function FocusModeWalkthrough() {
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    try {
      if (!localStorage.getItem(FLAG_KEY)) {
        // Tiny delay so the user perceives the page first, then the explainer.
        setTimeout(() => setShow(true), 450);
      }
    } catch {
      /* localStorage blocked — skip walkthrough silently */
    }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(FLAG_KEY, '1'); } catch { /* ignore */ }
    setShow(false);
  };

  const next = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else dismiss();
  };

  const prev = () => { if (step > 0) setStep(step - 1); };

  const current = STEPS[step];
  const Icon = current.icon;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(8,12,22,0.88)', backdropFilter: 'blur(8px)' }}
          data-testid="focus-walkthrough-modal"
          onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
        >
          <motion.div
            key={`step-${step}`}
            initial={{ y: 14, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 14, opacity: 0 }}
            transition={{ type: 'spring', damping: 22, stiffness: 240 }}
            className="w-full max-w-md bg-[#0C1222] border border-white/12 shadow-2xl"
          >
            {/* Top bar */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/8">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#5C8AFF]" />
                <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-white/55">
                  Focus mode · how it works
                </p>
              </div>
              <button
                onClick={dismiss}
                className="p-1.5 text-white/55 hover:text-white transition-colors"
                data-testid="focus-walkthrough-close"
                aria-label="Close walkthrough"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-7" data-testid={`focus-walkthrough-step-${step + 1}`}>
              <div
                className="w-12 h-12 flex items-center justify-center mb-5"
                style={{ backgroundColor: `${current.accent}1A`, color: current.accent }}
              >
                <Icon className="w-6 h-6" />
              </div>
              <h2 className="text-2xl font-bold text-white tracking-tight mb-3">
                {current.title}
              </h2>
              <div className="text-sm text-white/80 leading-relaxed">
                {current.body}
              </div>
            </div>

            {/* Progress dots */}
            <div className="flex items-center justify-center gap-1.5 pb-3">
              {STEPS.map((_, i) => (
                <div
                  key={`dot-${i}`}
                  className={`h-1 transition-all ${i === step ? 'w-6 bg-white' : 'w-1 bg-white/25'}`}
                />
              ))}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-5 py-4 border-t border-white/8 bg-white/[0.02]">
              <button
                onClick={prev}
                disabled={step === 0}
                className="inline-flex items-center gap-1 text-xs text-white/55 hover:text-white disabled:opacity-0 transition-colors"
                data-testid="focus-walkthrough-prev"
              >
                <ArrowLeft className="w-3 h-3" /> Back
              </button>
              <button
                onClick={dismiss}
                className="text-xs text-white/55 hover:text-white transition-colors"
                data-testid="focus-walkthrough-skip"
              >
                Skip
              </button>
              <button
                onClick={next}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#002FA7] hover:bg-[#1A47C2] text-white font-semibold text-xs tracking-tight transition-colors"
                data-testid="focus-walkthrough-next"
              >
                {step === STEPS.length - 1 ? (
                  <>I'm ready <Coffee className="w-3 h-3" /></>
                ) : (
                  <>Next <ArrowRight className="w-3 h-3" /></>
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
